<template>
  <div class="row justify-end">
    <q-chip
      :label="dateRangeLabel"
      icon-right="edit"
      square
      dense
      clickable
      @click="toggleDateRangeCalendar"
    />
    <div>
      <q-popup-proxy
        v-model="showDateRangeCalendar"
        transition-show="scale"
        transition-hide="scale"
      >
        <q-date
          :model-value="dateRangeValue"
          :options="isValidDate"
          range
          no-unset
          @range-start="selectStartDate"
          @update:model-value="updateDateRange"
        />
      </q-popup-proxy>
    </div>
  </div>
</template>

<script>

import { date } from 'quasar'
import { IsoDate } from 'src/models/model'
import TableCellMixin from 'src/modules/admin/components/model-table/components/TableCellMixin'
const { addToDate } = date

export default {
  name: 'BoxOfficeReportDateRangeCell',
  configuration: ['currentCircuit'],
  mixins: [TableCellMixin],
  data () {
    return {
      showDateRangeCalendar: false,
      showDateRangeCalendarSelect: false,
      showDateRangeCalendarSelectTimeout: null,
      selectedStartDate: null
    }
  },
  computed: {
    boxOfficeReport () {
      return this.props.row
    },
    movie () {
      return this.boxOfficeReport.movie
    },
    dateRangeLabel () {
      return [
        this.$i18n.d(new Date(this.boxOfficeReport.startDate), 'shortDate'),
        this.$i18n.d(new Date(this.boxOfficeReport.endDate), 'shortDate')
      ].join(' to ')
    },
    dateRangeValue () {
      return {
        from: this.boxOfficeReport.startDate.toDateString(),
        to: this.boxOfficeReport.endDate.toDateString()
      }
    },
    dateOfFirstShowing () {
      return new Date(this.movie.dateOfFirstShowing)
    },
    weekLength () {
      if (_.isNil(this.selectedStartDate)) return 6

      if (this.isPremiereDate(this.selectedStartDate)) {
        // Premiere week length
        // E.g. Thursday to Thursday or Wednesday to Thursday
        return 6 + (this.movie.boxOfficeWeekStartDay - this.dateOfFirstShowing.getDay())
      }

      // Default week length
      // E.g. Friday to Thursday
      return 6
    }
  },
  watch: {
    // When clicking the showDateRangeCalendar input, it dismisses the calendar if shown (as a click outside the calendar dialog dismisses it)
    // However, we want clicking the input to show the calendar if it is not already shown
    // As clicking the input dismisses a shown calendar, then toggles showDateRangeCalendar, it quickly sets the value to false then true
    // To avoid this flicker, we need to debounce showDateRangeCalendar so we can set it to true if it _was_ false, and keep it as false if it _was_ true
    showDateRangeCalendar () {
      if (!_.isNil(this.showDateRangeCalendarSelectTimeout)) {
        clearTimeout(this.showDateRangeCalendarSelectTimeout)
      }

      this.showDateRangeCalendarSelectTimeout = setTimeout(() => {
        this.showDateRangeCalendarSelect = this.showDateRangeCalendar
      }, 100)
    }
  },
  methods: {
    updateDateRange (dateRange, _reason, _details) {
      this.props.row.startDate = new IsoDate(dateRange.from)
      this.props.row.endDate = new IsoDate(dateRange.to)
      this.toggleDateRangeCalendar()
    },
    selectStartDate (value) {
      this.selectedStartDate = new Date(value.year, value.month - 1, value.day)
    },
    isValidDate (value) {
      if (_.isNil(this.selectedStartDate)) {
        return this.isValidStartDate(value)
      } else {
        return this.isValidEndDate(value)
      }
    },
    isValidStartDate (value) {
      const startDateOption = new Date(value)

      if (startDateOption.getDay() === this.movie.boxOfficeWeekStartDay) return true
      if (this.isPremiereDate(startDateOption)) return true

      return false
    },
    isPremiereDate (startDateOption) {
      if (!this.movie.boxOfficeWeekExtendPremieres) return false

      // Only the first non-preview Box Office Report can be a premiere week
      // This does mean different Box Office Reports can have different valid dates, but we don't intend
      // for this date editor to be used to swap weeks around
      if (this.boxOfficeReport.weekNumber !== 1 || this.boxOfficeReport.isPreview) return false

      // We explicitly compare date strings with added days here over any kind of <= && >= date comparison
      // The toDateString() comparisons are simpler, eliminating risk of timezone, specific time of day, or day of week math issues
      if (startDateOption.toDateString() === this.dateOfFirstShowing.toDateString()) return true
      if (startDateOption.toDateString() === addToDate(this.dateOfFirstShowing, { days: 1 }).toDateString()) return true
      if (startDateOption.toDateString() === addToDate(this.dateOfFirstShowing, { days: 2 }).toDateString()) return true

      return false
    },
    isValidEndDate (value) {
      const endDateOption = new Date(value)

      if (endDateOption.getTime() <= this.selectedStartDate.getTime()) return false
      if (endDateOption.getTime() > addToDate(this.selectedStartDate, { days: this.weekLength }).getTime()) return false
      return true
    },
    toggleDateRangeCalendar () {
      this.selectedStartDate = null
      this.showDateRangeCalendar = !this.showDateRangeCalendarSelect
    }
  }
}
</script>
